import type { EstimatedTimeNameRef } from '@gat-ship/types'
import { normalizeTimeString } from '@gat-ship/utils'

/**
 * The value emitted by {@link EstimatedTimePickerCombined}. One field does
 * double duty: a typed exact clock time, a picked named estimate, or an
 * unresolved draft the user must fix.
 *
 * - `exact` — a canonical `HH:mm` clock time (already normalized).
 * - `named` — the chosen {@link EstimatedTimeNameRef.name}.
 * - `invalid` — the kept raw text that resolved to neither a valid time nor
 *   a known name; carried so the input keeps showing it (and the field
 *   reads as incomplete) across a controlled-`modelValue` round-trip.
 * - `undefined` — empty, i.e. no time (date-only).
 */
export type EstimatedTimeChoice =
  | { kind: 'exact'; time: string }
  | { kind: 'named'; name: string }
  | { kind: 'invalid'; text: string }
  | undefined

/** How the current draft text is being interpreted. */
export type EstimatedTimeInputMode = 'empty' | 'exact' | 'named-search'

/**
 * The leading-character rule that drives the whole combined input: a digit
 * starts an exact clock time (the named list is suppressed), a letter starts
 * a name search, and empty means date-only. Leading whitespace is ignored.
 */
export function classifyEstimatedTimeInput(
  text: string
): EstimatedTimeInputMode {
  const trimmed = text.trim()
  if (!trimmed) return 'empty'
  return /^\d/.test(trimmed) ? 'exact' : 'named-search'
}

/**
 * Case-insensitive search over `name` + `description`, mirroring the plain
 * `EstimatedTimePicker`'s filter. Empty text returns every entry.
 */
export function filterEstimatedTimeEntries(
  entries: readonly EstimatedTimeNameRef[],
  text: string
): EstimatedTimeNameRef[] {
  const query = text.trim().toLowerCase()
  if (!query) return [...entries]
  return entries.filter(
    (entry) =>
      entry.name.toLowerCase().includes(query) ||
      entry.description.toLowerCase().includes(query)
  )
}

/**
 * Resolve the committed value from the current draft on Tab / Enter / blur:
 * - empty → `undefined` (date-only).
 * - exact mode → normalize; a valid time becomes `exact`, otherwise `invalid`.
 * - name-search → the keyboard-highlighted entry wins; else an exact
 *   (case-insensitive) name match; else the sole remaining filter match;
 *   otherwise `invalid` (the draft is kept for the user to fix).
 */
export function resolveEstimatedTimeChoice(params: {
  text: string
  entries: readonly EstimatedTimeNameRef[]
  highlighted?: EstimatedTimeNameRef
}): EstimatedTimeChoice {
  const { text, entries, highlighted } = params
  const mode = classifyEstimatedTimeInput(text)
  if (mode === 'empty') return undefined

  if (mode === 'exact') {
    const time = normalizeTimeString(text)
    return time
      ? { kind: 'exact', time }
      : { kind: 'invalid', text: text.trim() }
  }

  if (highlighted) return { kind: 'named', name: highlighted.name }

  const query = text.trim().toLowerCase()
  const exactName = entries.find((entry) => entry.name.toLowerCase() === query)
  if (exactName) return { kind: 'named', name: exactName.name }

  const matches = filterEstimatedTimeEntries(entries, text)
  if (matches.length === 1) return { kind: 'named', name: matches[0]!.name }

  return { kind: 'invalid', text: text.trim() }
}

/** The text an {@link EstimatedTimeChoice} shows in the input. */
export function displayEstimatedTimeChoice(
  choice: EstimatedTimeChoice
): string {
  if (!choice) return ''
  switch (choice.kind) {
    case 'exact':
      return choice.time
    case 'named':
      return choice.name
    case 'invalid':
      return choice.text
  }
}
